import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "../../../firebase";
import { useUserAuth } from "../../../context/userAuthContext";
import {
  CreditCard,
  AlertCircle,
  ArrowRight,
  Sparkles,
  Plus,
  Search,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  Shield,
  Calendar,
} from "lucide-react";

const CardsDashboard = () => {
  const { user } = useUserAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [allApplications, setAllApplications] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all"); // "all", "polizas", "tarjetas"

  useEffect(() => {
    if (user) {
      loadAllApplications();
    }
  }, [user]);

  const loadAllApplications = async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Cargar pólizas
      const policiesRef = collection(db, "policies");
      const policiesQuery = query(
        policiesRef,
        where("userId", "==", user.uid),
        orderBy("createdAt", "desc")
      );
      const policiesSnapshot = await getDocs(policiesQuery);
      const policiesData = [];
      policiesSnapshot.forEach((doc) => {
        policiesData.push({
          id: doc.id,
          ...doc.data(),
          tipo: "poliza",
        });
      });

      // Cargar solicitudes de tarjetas
      const cardsRef = collection(db, "cardApplications");
      const cardsQuery = query(
        cardsRef,
        where("userId", "==", user.uid),
        orderBy("createdAt", "desc")
      );
      const cardsSnapshot = await getDocs(cardsQuery);
      const cardsData = [];
      cardsSnapshot.forEach((doc) => {
        cardsData.push({
          id: doc.id,
          ...doc.data(),
          tipo: "tarjeta",
        });
      });

      // Combinar y ordenar por fecha
      const allData = [...policiesData, ...cardsData].sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
        return dateB - dateA;
      });

      setAllApplications(allData);
    } catch (error) {
      console.error("Error al cargar solicitudes:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (estado) => {
    const statusConfig = {
      pendiente: {
        icon: Clock,
        color: "bg-yellow-100 text-yellow-800 border-yellow-200",
        label: "Pendiente",
      },
      aprobada: {
        icon: CheckCircle,
        color: "bg-green-100 text-green-800 border-green-200",
        label: "Aprobada",
      },
      rechazada: {
        icon: XCircle,
        color: "bg-red-100 text-red-800 border-red-200",
        label: "Rechazada",
      },
      en_revision: {
        icon: AlertCircle,
        color: "bg-blue-100 text-blue-800 border-blue-200",
        label: "En Revisión",
      },
    };

    const config = statusConfig[estado] || statusConfig.pendiente;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border ${config.color}`}
      >
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getApplicationLabel = (application) => {
    if (application.tipo === "poliza") {
      const policyTypes = {
        seguro_viajero: "Seguro de Viajero",
        seguro_auto: "Seguro de Auto",
        seguro_hogar: "Seguro de Hogar",
        seguro_vida: "Seguro de Vida",
        seguro_gmm: "Gastos Médicos Mayores",
      };
      return policyTypes[application.tipoPoliza] || application.tipoPoliza;
    } else {
      return application.tarjetaAplicando || "American Express";
    }
  };

  const getApplicationIcon = (application) => {
    if (application.tipo === "poliza") {
      return Shield;
    } else {
      return CreditCard;
    }
  };

  const getApplicationColor = (application) => {
    if (application.tipo === "poliza") {
      return "from-[#218e01] to-[#5fc500]";
    } else {
      return "from-blue-600 to-blue-800";
    }
  };

  const filteredApplications = allApplications.filter((app) => {
    const matchesSearch =
      getApplicationLabel(app)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      app.nombreCompleto?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.nombres?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || app.estado === filterStatus;
    const matchesType = filterType === "all" || app.tipo === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  const cardTypes = [
    {
      id: "amex",
      title: "American Express",
      description: "Tarjetas de crédito premium con beneficios exclusivos",
      icon: CreditCard,
      color: "from-blue-600 to-blue-800",
      bgColor: "bg-blue-50",
      features: [
        "Puntos de recompensa",
        "Acceso a salas VIP",
        "Seguro de viaje incluido",
        "Asistencia 24/7",
      ],
      path: "/cards/amex-application",
      logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/American_Express_logo_%282018%29.svg/2052px-American_Express_logo_%282018%29.svg.png",
    },
  ];

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <div className="text-center bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-slate-200">
          <AlertCircle className="w-16 h-16 text-[#096365] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Acceso no autorizado
          </h2>
          <p className="text-[#096365]">Por favor inicia sesión para continuar</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 pb-20 sm:pb-8">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-lg border-b border-slate-200/50 sticky top-16 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg shadow-md">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                Tarjetas de Crédito
              </h1>
              <p className="text-xs text-slate-600">
                Solicita tu tarjeta de crédito premium
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Mis Solicitudes - Compacto */}
        <div className="mb-8 bg-white rounded-xl p-4 shadow-md border border-slate-200/50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-bold text-slate-900">Mis Solicitudes</h2>
              <button
                onClick={() => navigate("/cards/amex-application")}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg text-xs font-semibold hover:shadow-lg transition-all"
              >
                <Plus className="w-3 h-3" />
                Nueva
              </button>
            </div>
            <div className="flex gap-2">
              <div className="flex-1 relative max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar..."
                  className="w-full pl-9 pr-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all bg-white"
                />
              </div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all bg-white"
              >
                <option value="all">Todas</option>
                <option value="poliza">Pólizas</option>
                <option value="tarjeta">Tarjetas</option>
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all bg-white"
              >
                <option value="all">Todos</option>
                <option value="pendiente">Pendiente</option>
                <option value="en_revision">En Revisión</option>
                <option value="aprobada">Aprobada</option>
                <option value="rechazada">Rechazada</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-4">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : filteredApplications.length > 0 ? (
            <div className="space-y-2">
              {filteredApplications.map((app) => {
                const Icon = getApplicationIcon(app);
                return (
                  <div
                    key={app.id}
                    className="flex items-center justify-between p-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className={`p-1.5 bg-gradient-to-r ${getApplicationColor(app)} rounded-lg flex-shrink-0`}>
                        <Icon className="w-3 h-3 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-900 truncate">
                          {getApplicationLabel(app)}
                        </p>
                        <p className="text-xs text-slate-500">
                          {app.createdAt?.toDate
                            ? app.createdAt.toDate().toLocaleDateString("es-MX")
                            : "Fecha no disponible"}
                        </p>
                      </div>
                      {getStatusBadge(app.estado)}
                    </div>
                    <button
                      onClick={() => {
                        if (app.tipo === "poliza") {
                          navigate(`/polizas/${app.id}`);
                        } else {
                          navigate(`/cards/${app.id}`);
                        }
                      }}
                      className="ml-2 p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                    >
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
              {filteredApplications.length > 0 && (
                <div className="pt-2 border-t border-slate-200">
                  <button
                    onClick={() => navigate("/cards/amex-application")}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg text-xs font-semibold hover:shadow-lg transition-all"
                  >
                    <Plus className="w-4 h-4" />
                    Crear Nueva Solicitud
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-xs text-slate-500 mb-3">
                {searchTerm || filterStatus !== "all" || filterType !== "all"
                  ? "No se encontraron solicitudes"
                  : "No hay solicitudes"}
              </p>
              <button
                onClick={() => navigate("/cards/amex-application")}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg text-xs font-semibold hover:shadow-lg transition-all"
              >
                <Plus className="w-4 h-4" />
                Nueva Solicitud
              </button>
            </div>
          )}
        </div>

        {/* Tipos de Tarjetas */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                Tarjetas Disponibles
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Selecciona la tarjeta que deseas solicitar
              </p>
            </div>
            <button
              onClick={() => navigate("/polizas")}
              className="hidden sm:flex items-center gap-2 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
            >
              <Shield className="w-4 h-4" />
              Ver Pólizas
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cardTypes.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.id}
                  className="group bg-white rounded-2xl sm:rounded-3xl p-6 shadow-lg border border-slate-200/50 hover:shadow-2xl transition-all duration-300 cursor-pointer"
                  onClick={() => navigate(card.path)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className={`p-4 bg-gradient-to-r ${card.color} rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300`}
                    >
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                  </div>

                  {card.logo && (
                    <div className="mb-4">
                      <img
                        src={card.logo}
                        alt={card.title}
                        className="h-12 object-contain"
                      />
                    </div>
                  )}

                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    {card.title}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">
                    {card.description}
                  </p>

                  <ul className="space-y-2 mb-6">
                    {card.features.slice(0, 3).map((feature, index) => (
                      <li
                        key={index}
                        className="flex items-center gap-2 text-sm text-slate-700"
                      >
                        <Sparkles className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-xl font-semibold hover:shadow-lg transition-all group-hover:scale-105"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(card.path);
                    }}
                  >
                    Solicitar Ahora
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardsDashboard;

